import { Service } from '@toeverything/infra';
import type { Subscription } from 'rxjs';

import { AccessibleDocCacheStore } from '../doc/stores/accessible-doc-cache';
import { WorkspaceAreaCacheStore } from '../folder-api/stores/workspace-area-cache';
import { AccessibleFolderCacheStore } from '../organize/stores/accessible-folder-cache';
import { TagStore } from '../tag/stores/tag';
import { WorkspaceService } from '../workspace/services/workspace';
import { WorkspacesService } from '../workspace/services/workspaces';
import { recordWorkspaceProjectionDiagnostic } from './diagnostics';
import type {
  WorkspaceMetadataChange,
  WorkspacePermissionChange,
} from './types';

/**
 * One workspace-scoped owner for server projection invalidations.
 *
 * UI routes and individual stores must not subscribe to workspace mutation
 * streams independently. This service turns each server event into one typed
 * reconciliation pass; sidebar, /all and /trash then converge through the same
 * document/folder/area stores.
 */
export class WorkspaceProjectionReconciler extends Service {
  private permissionSubscription: Subscription | null = null;
  private metadataSubscription: Subscription | null = null;

  constructor(
    private readonly workspaceService: WorkspaceService,
    private readonly workspacesService: WorkspacesService,
    private readonly accessibleDocCache: AccessibleDocCacheStore,
    private readonly accessibleFolderCache: AccessibleFolderCacheStore,
    private readonly workspaceAreaCache: WorkspaceAreaCacheStore,
    private readonly tagStore: TagStore
  ) {
    super();
  }

  prime(): void {
    const workspace = this.workspaceService.workspace;
    if (!workspace || workspace.flavour === 'local') return;

    if (!this.permissionSubscription) {
      this.permissionSubscription =
        workspace.engine.permissionsChanged$?.subscribe(event => {
          this.reconcilePermissionChange(event);
        }) ?? null;
    }
    if (!this.metadataSubscription) {
      this.metadataSubscription =
        workspace.engine.workspaceMetaChanged$?.subscribe(event => {
          this.reconcileMetadataChange(event);
        }) ?? null;
    }
  }

  reconcilePermissionChange(event: WorkspacePermissionChange): void {
    const workspace = this.workspaceService.workspace;
    if (!workspace || event.workspaceId !== workspace.id) return;

    recordWorkspaceProjectionDiagnostic({
      stage: 'reconciler:permission-received',
      changeType: event.changeType,
      docIds: event.affectedDocIds,
      folderIds: [
        ...(event.affectedFolderIds ?? []),
        ...(event.revokedFolderIds ?? []),
      ],
    });

    if (event.changeType === 'workspace_member_revoked') {
      void this.handleWorkspaceRevocation(event.workspaceId);
      return;
    }

    if (event.revokedFolderIds?.length) {
      const revokedDocIds =
        this.accessibleFolderCache.removeRevokedFolderSubtreesLocally(
          event.revokedFolderIds
        );
      this.accessibleDocCache.removeRevokedDocsLocally(revokedDocIds);
    }

    this.accessibleDocCache.reconcilePermissionChange(event);
    const reconciledFolderDocs =
      this.accessibleFolderCache.reconcilePermissionChange(event);
    this.workspaceAreaCache.reconcilePermissionChange(event);
    if (reconciledFolderDocs) {
      void reconciledFolderDocs
        .then(({ grantedDocIds, revokedDocIds }) => {
          if (revokedDocIds.length > 0) {
            this.accessibleDocCache.removeRevokedDocsLocally(revokedDocIds);
          }
          const eventDocIds = new Set(event.affectedDocIds ?? []);
          const docsToHydrate = grantedDocIds.filter(
            docId => !eventDocIds.has(docId)
          );
          if (docsToHydrate.length === 0) {
            return;
          }
          return this.accessibleDocCache.fetchDocsByIds(
            docsToHydrate,
            undefined,
            {
              diagnosticReason: `permission-event:${event.changeType}`,
              permissionGraphVersion: event.permissionGraphVersion,
              removeMissing: true,
            }
          );
        })
        .catch(() => {
          // Reconnect/visibility reconciliation retries transient failures.
        });
    }

    if (
      event.changeType === 'workspace_member_joined' ||
      event.changeType === 'workspace_member_role_changed' ||
      event.changeType === 'workspace_member_left'
    ) {
      this.workspacesService.list.revalidate();
      void this.workspaceAreaCache.refresh().catch(() => {});
    }
  }

  reconcileMetadataChange(event: WorkspaceMetadataChange): void {
    const workspace = this.workspaceService.workspace;
    if (!workspace || event.workspaceId !== workspace.id) return;

    recordWorkspaceProjectionDiagnostic({
      stage: 'reconciler:metadata-received',
      changeType: event.changeType,
      docIds: event.docId ? [event.docId] : undefined,
    });

    this.accessibleDocCache.reconcileMetadataChange(event);
    this.accessibleFolderCache.reconcileMetadataChange(event);
    this.workspaceAreaCache.reconcileMetadataChange(event);
    this.tagStore.reconcileMetadataChange(event);

    if (event.changeType === 'workspace_deleted') {
      this.workspacesService.list.revalidate();
    }
  }

  private async handleWorkspaceRevocation(workspaceId: string): Promise<void> {
    if (typeof window === 'undefined') return;
    try {
      const databases = await window.indexedDB?.databases?.();
      for (const database of databases ?? []) {
        if (database.name?.includes(workspaceId)) {
          window.indexedDB.deleteDatabase(database.name);
        }
      }
    } finally {
      window.location.href = '/';
    }
  }

  override dispose(): void {
    this.permissionSubscription?.unsubscribe();
    this.permissionSubscription = null;
    this.metadataSubscription?.unsubscribe();
    this.metadataSubscription = null;
    super.dispose();
  }
}
